<template>
  <div class="sc-daily-report">
    <DxDataGrid
      :ref="gridRefKey"
      :data-source="scDataSource"
      :remote-operations="gridRemoteOperations"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :show-column-lines="true"
      :show-row-lines="true"
      :column-min-width="120"
      :columns="gridColumns"
      :on-exporting="onExporting"
      :word-wrap-enabled="true"
      :element-attr="{ class: 'sc-daily-report-dxgrid' }"
      :row-height="50"
    >
      <DxPaging :total="3500" :page-size="pageSize" />
      <DxPager
        :visible="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :show-page-size-selector="true"
        :allowed-page-sizes="pageSizes"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="false" />
      <DxColumnChooser :enabled="true" mode="select" />
      <DxExport :enabled="true" :formats="['xlsx']" :allow-export-selected-data="false" />

      <DxToolbar>
        <DxItem location="before" template="filterButton" />
        <DxItem location="after" name="exportButton" />
        <DxItem location="after" name="columnChooserButton" />
      </DxToolbar>

      <template #filterButton>
        <div class="filters-icon">
          <span class="filters-title">{{ $t("common.filters") }}</span>
          <span class="filters-icon-circle" @click="onFiltersClick">
            <img src="@/assets/images/desktop/icons/filters-round.svg" alt="" />
          </span>
        </div>
      </template>

      <DxMasterDetail :enabled="true" template="detailTemplate" :autoExpandAll="false" />

      <template #detailTemplate="{ data }">
        <ScDailyReportDetailList :dailyData="data.data" ref="scDailyReportDetailList" />
      </template>

      <DxHeaderFilter :visible="false" />
      <DxScrolling show-scrollbar="always" :use-native="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
    </DxDataGrid>

    <GridFilterDialog
      :show="showGridFilterDialog"
      :data="gridFiltersDialogData"
      :def_work_started_at="filters.work_started_at"
      :def_work_ended_at="filters.work_ended_at"
      @onOK="onGridFiltersChanged"
      @close="() => (showGridFilterDialog = false)"
      ref="filtersDialog"
    ></GridFilterDialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { permissions, permissionTypes } from "~/helpers/Permissions";
import GridFilterDialog from "~/pages/salary-calculator/sc-work-reports/partials/GridFilterDialog.vue";
import ScDailyReportDetailList from "~/pages/salary-calculator/sc-work-reports/partials/ScDailyReportDetailList.vue";
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxColumnChooser,
  DxExport,
  DxToolbar,
  DxItem,
  DxMasterDetail,
  DxScrolling,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { exportDataGrid } from "devextreme/excel_exporter";
import { DxButton } from "devextreme-vue/button";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";

export default {
  name: "SCDailyReportDx",
  components: {
    GridFilterDialog,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxHeaderFilter,
    DxFilterRow,
    DxColumnChooser,
    DxExport,
    DxToolbar,
    DxItem,
    DxButton,
    DxMasterDetail,
    ScDailyReportDetailList,
    DxScrolling,
  },
  props: ["data"],
  data() {
    return {
      gridRefKey: "dailyReportDxGrid",
      is_loading: false,
      showGridFilterDialog: false,
      filters: (() => {
        const now = new Date();
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const fmt = (d) => {
          const y = d.getFullYear();
          const m = ("0" + (d.getMonth() + 1)).slice(-2);
          const dd = ("0" + d.getDate()).slice(-2);
          return `${y}-${m}-${dd}`;
        };
        return {
          work_started_at: fmt(sixMonthsAgo),
          work_ended_at: fmt(now),
        };
      })(),
      gridFiltersDialogData: {},
      pageSize: 15,
      pageSizes: [15, 50, 100, 1000],
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      scDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          /*let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;*/

          let payload = {
            /*enablePagination: true,
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,*/
          };

          if (this.filters) {
            payload = { ...payload, ...this.filters };
          }

          try {
            await this.getSCWorkCollectorHectareData(payload).then((response) => {
              dataSource.data = response.data;
              dataSource.totalCount = response.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching work collector hectare data:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
    };
  },
  computed: {
    config() {
      return {
        permission: permissions.SC_REPORTS,
        permission_type: permissionTypes.module,
      };
    },
    scDataGridInstance: function () {
      return this.$refs[this.gridRefKey].instance;
    },
    gridColumns() {
      const headers = [
        {
          name: this.$t("common.work_date"),
          key: "work_date",
          align: "center",
          disabledSelection: true,
          sortable: false,
        },
        {
          name: this.$t("common.employee_name"),
          key: "employee_name",
          align: "center",
          sortable: false,
          disabledSelection: true,
        },
        { name: this.$t("common.role"), key: "employee_role", align: "center", sortable: false },
        {
          name: this.$t("salary_calculator.reports.table.work_hours_total"),
          key: "summary.active_work_hours",
          align: "center",
          sortable: false,
          dataType: "number",
          format: { type: "fixedPoint", precision: 2 },
        },
        {
          name: this.$t("salary_calculator.reports.table.supplimentar_hours"),
          key: "summary.total_supplimentar_hours",
          align: "center",
          sortable: false,
          dataType: "number",
          format: { type: "fixedPoint", precision: 2 },
        },
        {
          name: this.$t("salary_calculator.reports.table.hectare_data_total"),
          key: "summary.total_hectare_worked",
          align: "center",
          sortable: false,
          dataType: "number",
          format: { type: "fixedPoint", precision: 2 },
        },
        { name: this.$t("common.holiday_day"), key: "is_holiday", align: "center", sortable: false },
        { name: this.$t("salary_calculator.reports.table.check_in"), key: "summary.check_in_datetime", align: "center", sortable: false },
        { name: this.$t("salary_calculator.reports.table.check_out"), key: "summary.check_out_datetime", align: "center", sortable: false },
        {
          name: this.$t("salary_calculator.reports.table.total_hours"),
          key: "summary.total_work_hours_utc",
          align: "center",
          sortable: false,
        },
        {
          name: this.$t("salary_calculator.reports.table.total_inactive_hours"),
          key: "summary.total_non_productive_hours_utc",
          align: "center",
          sortable: false,
        },
        {
          name: this.$t("salary_calculator.reports.table.salary"),
          key: "summary.salary",
          align: "center",
          sortable: false,
          dataType: "number",
          format: { type: "fixedPoint", precision: 2 },
        },
      ];
      return (headers || [])
        .filter((h) => h && h.key)
        .map((h) => {
          let col = {
            dataField: h.key,
            caption: h.name,
            alignment: h.align || undefined,
            allowSorting: h.sortable !== false,
            dataType: h.dataType || undefined,
            format: h.format || undefined,
          };

           // Keep essential columns fixed and non-hideable
          if (h.key === "work_date" || h.key === "employee_name") {
            col.fixed = true;
            col.fixedPosition = "left";
            col.allowHiding = false;
            col.showInColumnChooser = false;
            col.visible = true;
          }

          if (h.key === "is_holiday") {
            col.dataType = "string";
            col.calculateDisplayValue = (row) => (Number(row?.is_holiday) === 1 ? this.$t("common.yes") : this.$t("common.no"));
          }

          return col;
        });
    },
  },
  methods: {
    ...mapActions({
      getSCWorkCollectorHectareData: "scWorkPivot/getSCWorkCollectorHectareData",
    }),
    onPageSelected(page_id) {
      this.onPaginate({ next_page: page_id });
    },
    onFiltersClick() {
      this.gridFiltersDialogData = {
        filters: this.filters,
      };
      this.showGridFilterDialog = true;
    },
    onGridFiltersChanged(filters) {
      this.filters = filters;
      this.showGridFilterDialog = false;
      this.scDataGridInstance.refresh();
    },
    getCurrentDate() {
      const d = new Date();
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}_${pad(d.getMonth() + 1)}_${pad(d.getDate())}_${pad(d.getHours())}_${pad(d.getMinutes())}_${pad(
        d.getSeconds()
      )}`;
    },
    getDetailColumnsForExport() {
      let ref = this.$refs.scDailyReportDetailList;
      if (Array.isArray(ref)) ref = ref[0];
      if (ref && typeof ref.getGridColumns === "function") {
        try {
          const cols = ref.getGridColumns();
          if (Array.isArray(cols) && cols.length)
            return cols.map((c) => ({
              dataField: c.dataField,
              caption: c.caption,
              dataType: c.dataType,
              format: c.format,
            }));
        } catch (_) {}
      }
      // Fallback mirroring ScDailyReportDetailList headers
      return [
        { caption: this.$t("common.work_name"), dataField: "work_name" },
        { caption: this.$t("common.work_hours"), dataField: "calculated_active_work_hours_utc" },
        { caption: this.$t("common.work_group"), dataField: "work_group_name" },
        { caption: this.$t("salary_calculator.reports.table.hectare"), dataField: "hectare_worked" },
        { caption: this.$t("salary_calculator.reports.table.idle_engine_time"), dataField: "inactive_work_hours_utc" },
        { caption: this.$t("salary_calculator.reports.table.tariff"), dataField: "tariff" },
        { caption: this.$t("salary_calculator.reports.table.cost"), dataField: "cost" },
      ];
    },
    onExporting(e) {
      const fileBaseName = "sc_daily_report";
      if (e.format !== "xlsx") return;

      // Cancel default export to run a customized one
      e.cancel = true;

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Report");

      // Collect mapping of Excel row -> original data row
      const rowMap = new Map();
      let headerRowIndex = null;
      let firstHeaderColIndex = null;

      // Helper to get detail grid columns (fallback to static definition if ref not available)
      const detailCols = this.getDetailColumnsForExport();

      exportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
        customizeCell: ({ gridCell, excelCell }) => {
          if (!gridCell) return;
          // Capture header row index and starting column using the first column header
          if (gridCell.rowType === "header" && gridCell.column && !headerRowIndex) {
            headerRowIndex = excelCell.row;
            firstHeaderColIndex = excelCell.column;
          }
          // Capture one data mapping per row (use first column in the grid as the anchor)
          if (gridCell.rowType === "data" && gridCell.column && gridCell.column.dataField === "work_date") {
            rowMap.set(excelCell.row, gridCell.data);
          }
        },
      }).then(() => {
        // Insert detail columns after the 3rd exported column
        // Use detected header start when available to avoid misalignment
        const firstCol = firstHeaderColIndex || 1;
        const insertAt = firstCol + 3; // after third column
        for (let j = 0; j < detailCols.length; j++) {
          worksheet.spliceColumns(insertAt + j, 0, []);
        }

        // Write detail headers
        if (headerRowIndex) {
          const headerRow = worksheet.getRow(headerRowIndex);
          detailCols.forEach((c, j) => {
            const cell = headerRow.getCell(insertAt + j);
            cell.value = c.caption;
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.font = { bold: true };
          });
        }

        // For each master row, insert its detail rows and apply styling
        const rowEntriesDesc = Array.from(rowMap.entries()).sort((a, b) => b[0] - a[0]);
        const widthStats = detailCols.map((c) => (c.caption ? String(c.caption).length : 10));
        const lastCol = worksheet.columnCount;
        const masterFill = { type: "pattern", pattern: "solid", fgColor: { argb: "bebebe" } }; // dark gray
        const detailFill = { type: "pattern", pattern: "solid", fgColor: { argb: "f1f1f1" } }; // light gray

        for (const [rowIdx, master] of rowEntriesDesc) {
          const details = Array.isArray(master?.works) ? master.works : Array.isArray(master?.rows) ? master.rows : [];
          const count = details.length;
          if (!count) continue;

          // Insert blank rows immediately after the master row
          const blanks = new Array(count).fill([]);
          worksheet.spliceRows(rowIdx + 1, 0, ...blanks);

          // Shade the master row across all columns
          const masterRow = worksheet.getRow(rowIdx);
          for (let c = 1; c <= lastCol; c++) {
            masterRow.getCell(c).fill = masterFill;
          }

          // Fill detail values into inserted rows
          for (let i = 0; i < count; i++) {
            const detail = details[i] || {};
            const excelRow = worksheet.getRow(rowIdx + 1 + i);
            // Clear first three columns once to avoid duplicated values if merge doesn't render
            for (let c = 0; c < 3; c++) {
              const colIndex = firstCol + c;
              excelRow.getCell(colIndex).value = null;
            }
            // Shade detail row across all columns
            for (let c = 1; c <= lastCol; c++) {
              excelRow.getCell(c).fill = detailFill;
            }
            for (let j = 0; j < detailCols.length; j++) {
              const col = detailCols[j];
              let val = null;
              // Support nested fields "a.b.c"
              if (col.dataField) {
                const path = col.dataField.split(".");
                val = path.reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), detail);
              }
              const cell = excelRow.getCell(insertAt + j);
              cell.value = val;
              cell.alignment = { horizontal: "center", vertical: "middle" };
              if (typeof val === "number" || col.dataType === "number") {
                cell.numFmt = col.format && col.format.precision != null ? "0." + "0".repeat(col.format.precision) : "0.00";
              }
              const repr = val != null ? String(val) : "";
              if (repr.length > widthStats[j]) widthStats[j] = repr.length;
            }
          }
        }

        // Set approximate auto-width for detail columns using content lengths
        for (let j = 0; j < detailCols.length; j++) {
          const col = worksheet.getColumn(insertAt + j);
          const isNumber = detailCols[j].dataType === "number";
          const targetWidth = isNumber ? 12 : Math.max(10, Math.min(40, (widthStats[j] || 10) + 2));
          col.width = targetWidth;
        }

        // Save file
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileBaseName}_${this.getCurrentDate()}.xlsx`);
        });
      });
    },
  },
};
</script>

<style scoped>
.filters-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 50px;
  padding-left: 10px;
  margin: 0px;
  background: #f3f4f6;
  border: none;
}

.sc-daily-report-dxgrid {
  height: 80vh;
}
</style>

<style>
.sc-daily-report-dxgrid .dx-datagrid-header-panel .dx-toolbar {
  margin-bottom: 20px !important;
  padding-top: 0px;
  padding-right: 10px;
}
</style>
